import type { ChangeEvent, FormEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

export type TextFieldEditProps = {
  value: string;
  onSubmit: (newValue: string, id: string) => void;
  helperText?: string;
  id?: string;
  disableEdit?: boolean;
  hideEdit?: boolean;
  // when using somewhere that you can always edit and don't want it to be affected by the global edit mode
  overrideEditMode?: boolean;
  multiline?: boolean;
  staticContent: string | null;
  editMode?: boolean;
  type?: TextFieldProps["type"];
  inputProps?: TextFieldProps["inputProps"];
};

const TextFieldEdit = ({
  value,
  onSubmit,
  helperText,
  disableEdit = false,
  hideEdit = false,
  overrideEditMode,
  multiline = false,
  id = "",
  staticContent,
  editMode = true,
  type,
  inputProps,
}: TextFieldEditProps) => {
  const [editingText, setEditingText] = useState<boolean>(false);
  const [text, setText] = useState<string>(value);

  // this component likely shouldn't manage text and editing text the way it does, but this fixes the issue for now
  useEffect(() => {
    setText(value);
  }, [value]);

  const handleChangeText = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setText(event.target.value),
    [setText],
  );

  const handleEditText = useCallback(() => {
    setEditingText(true);
  }, [setEditingText]);

  const handleClose = useCallback(() => {
    setEditingText(false);
    setText(value);
  }, [value, setEditingText]);

  const handleSave = useCallback(() => {
    onSubmit(text, id);
    handleClose();
  }, [text, onSubmit, handleClose, id]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSave();
    },
    [handleSave],
  );

  useEffect(() => {
    !editMode && !overrideEditMode && handleClose();
  }, [editMode, handleClose, overrideEditMode]);

  if (!editingText) {
    return (
      <>
        <Box display="flex" alignItems="center">
          <Tooltip title={staticContent ?? ""}>
            <Typography
              sx={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "auto",
              }}
            >
              {staticContent}
            </Typography>
          </Tooltip>

          {(editMode || overrideEditMode) && !hideEdit && (
            <IconButton disabled={disableEdit} size="small" onClick={handleEditText} sx={{ pl: 1 }}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
        {helperText && (
          <Typography variant="caption" color="textSecondary">
            {helperText}
          </Typography>
        )}
      </>
    );
  }

  return (
    <Box component="form" display="flex" flexDirection="column" onSubmit={handleSubmit}>
      <Box display="flex" alignItems="center">
        <TextField
          defaultValue={value}
          onChange={handleChangeText}
          margin="none"
          fullWidth
          multiline={multiline}
          sx={{ maxWidth: { xs: "15rem", sm: "20rem", md: "25rem" } }}
          type={type}
          inputProps={inputProps}
        />
        <IconButton size="small" onClick={handleSave} disabled={text === value}>
          <CheckCircleIcon />
        </IconButton>
        <IconButton size="small" onClick={handleClose}>
          <CancelOutlinedIcon />
        </IconButton>
      </Box>
      <Typography variant="caption" color="textSecondary" ml={1} mt={0.5}>
        {helperText}
      </Typography>
    </Box>
  );
};

export default TextFieldEdit;
